import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchAuditByCorrelation } from "../../api/auditApi";
import { Log } from "../../models/Log";
import ExpandableItemsList from "../../components/lists/ExpandableItemsList";

const AuditCorrelationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const {
    data: auditEntries = [],
    isFetching,
    error,
  } = useQuery<Log[]>(
    ["AuditCorrelation", id],
    () => fetchAuditByCorrelation(id!),
    {
      enabled: !!id,
    }
  );

  if (isFetching)
    return <div className="p-6">Loading audit correlation data...</div>;

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading audit data
              </h3>
              <p className="mt-2 text-sm text-red-700">
                Unable to load audit entries for correlation ID: {id}
              </p>
            </div>
          </div>
        </div>
        <Link
          to="/audit"
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-full text-[#0F0F0F] bg-[#F4A832] hover:bg-[#e09620]"
        >
          Back to Audit
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl sm:px-6 mt-3 lg:px-8">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[15px] font-semibold text-[var(--sc-text)]">Audit</h1>
            <p className="mt-1 text-sm text-[var(--sc-muted)]">
              Correlation ID:{" "}
              <span className="font-mono bg-[var(--sc-bg)] px-2 py-1 rounded">
                {id}
              </span>
            </p>
          </div>
          <Link
            to="/audit"
            className="inline-flex items-center px-4 py-2 border border-[var(--sc-border)] text-sm font-medium rounded-full text-[var(--sc-text)] bg-[var(--sc-surface)] hover:bg-[var(--sc-bg)]"
          >
            ← Back to Audit
          </Link>
        </div>
      </div>

      {auditEntries.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-[var(--sc-muted)]">
            No audit entries found for correlation ID: {id}
          </div>
        </div>
      ) : (
        <>
          <div className="mb-4">
            <p className="text-sm text-[var(--sc-muted)]">
              Found {auditEntries.length} audit{" "}
              {auditEntries.length === 1 ? "entry" : "entries"} for this
              correlation
            </p>
          </div>

          <ExpandableItemsList
            data={auditEntries.map((log) => ({
              id: log.id,
              name: `${log.action || "Unknown Action"} - ${log.tableName}`,
              description: `Resource ID: ${log.resourceId}`,
              oldValues: log.oldValues,
              newValues: log.newValues,
              created: new Date(log.created),
              createdBy: log.createdBy,
              edited: new Date(log.edited),
              EditedBy: log.editedBy,
              resourceId: log.resourceId,
              tableName: log.tableName,
              correlationId: log.correlationId,
            }))}
            isLoading={isFetching}
            urlPrefix="/audit/"
          />
        </>
      )}
    </div>
  );
};

export default AuditCorrelationPage;
